import React, { useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
  useGetTenantCacheStatusMutation,
  useGetUserProfileCacheStatusMutation,
  useClearUserProfileCacheMutation,
  useGetProductCacheStatusMutation,
  useClearProductCacheMutation,
  useClearTenantRulesCacheMutation,
  useClearSchemesCacheMutation,
  useClearCompanyCacheMutation,
  useClearAllUserProfileCachesMutation,
  useClearAllProductCachesMutation,
  useClearAllTenantCachesMutation,
} from "../../../features/tenant/cacheManagement/cacheManagementApi";

const auth = () => ({
  token: sessionStorage.getItem("access_token"),
  tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
});

const fmtTtl = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.round(sec / 3600)}h`;
};

const fmtAgo = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

function StatusBadge({ active }) {
  if (active === null)
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
        N/A
      </span>
    );
  return active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      Cached
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
      Not Cached
    </span>
  );
}

function CacheCard({ label, data, onFlush, flushing }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        <StatusBadge active={data?.active ?? null} />
      </div>
      <div className="space-y-1.5 text-xs text-gray-500">
        {data?.keyCount != null && (
          <div className="flex justify-between">
            <span>Keys</span>
            <span className="font-medium text-gray-700 font-mono">{data.keyCount}</span>
          </div>
        )}
        {data?.ttlSeconds != null && (
          <div className="flex justify-between">
            <span>TTL Remaining</span>
            <span className="font-medium text-gray-700 font-mono">{fmtTtl(data.ttlSeconds)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Configured TTL</span>
          <span className="text-gray-600">{data?.configuredTtl || "—"}</span>
        </div>
        {data?.lastFlushed && (
          <div className="flex justify-between">
            <span>Last Flushed</span>
            <span className="text-gray-600">{fmtAgo(data.lastFlushed)}</span>
          </div>
        )}
      </div>
      <button
        onClick={onFlush}
        disabled={flushing}
        className="mt-auto w-full text-xs font-semibold py-1.5 px-3 rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {flushing ? "Flushing..." : "Flush Cache"}
      </button>
    </div>
  );
}

export default function CacheManagement() {
  const [status, setStatus] = useState(null);
  const [flushingKey, setFlushingKey] = useState(null);

  // User profile search
  const [mobileInput, setMobileInput] = useState("");
  const [userResult, setUserResult] = useState(null);

  // Product search
  const [codeInput, setCodeInput] = useState("");
  const [productResult, setProductResult] = useState(null);

  const [getStatus, { isLoading: loadingStatus }] = useGetTenantCacheStatusMutation();
  const [getUserStatus, { isLoading: loadingUser }] = useGetUserProfileCacheStatusMutation();
  const [clearUser, { isLoading: clearingUser }] = useClearUserProfileCacheMutation();
  const [getProductStatus, { isLoading: loadingProduct }] = useGetProductCacheStatusMutation();
  const [clearProduct, { isLoading: clearingProduct }] = useClearProductCacheMutation();
  const [clearRules] = useClearTenantRulesCacheMutation();
  const [clearSchemes] = useClearSchemesCacheMutation();
  const [clearCompany] = useClearCompanyCacheMutation();
  const [clearAllUsers] = useClearAllUserProfileCachesMutation();
  const [clearAllProducts] = useClearAllProductCachesMutation();
  const [clearAll] = useClearAllTenantCachesMutation();

  const fetchStatus = useCallback(async () => {
    const res = await getStatus(auth());
    if (res?.data?.status) {
      setStatus(res.data.body);
      toast.success("Status refreshed");
    } else {
      toast.error(res?.data?.message || "Failed to load status");
    }
  }, [getStatus]);

  const flush = useCallback(async (key, fn, label) => {
    setFlushingKey(key);
    const res = await fn(auth());
    setFlushingKey(null);
    if (res?.data?.status) {
      toast.success(res.data.message);
      fetchStatus();
    } else {
      toast.error(res?.data?.message || `Failed to flush ${label}`);
    }
  }, [fetchStatus]);

  const handleFlushAll = () => {
    if (!window.confirm("This will flush ALL caches for this tenant. Are you sure?")) return;
    flush("all", clearAll, "all caches");
  };

  // User profile lookup
  const lookupUser = async () => {
    if (!mobileInput.trim()) return;
    setUserResult(null);
    const res = await getUserStatus({ ...auth(), mobile: mobileInput.trim() });
    if (res?.data?.status) setUserResult(res.data.body);
    else toast.error(res?.data?.message || "User not found");
  };

  const flushUser = async () => {
    const res = await clearUser({ ...auth(), mobile: mobileInput.trim() });
    if (res?.data?.status) {
      toast.success(res.data.message);
      setUserResult(null);
    } else {
      toast.error(res?.data?.message || "Failed to flush user cache");
    }
  };

  // Product lookup
  const lookupProduct = async () => {
    if (!codeInput.trim()) return;
    setProductResult(null);
    const res = await getProductStatus({ ...auth(), code: codeInput.trim() });
    if (res?.data?.status) setProductResult(res.data.body);
    else toast.error(res?.data?.message || "Failed to lookup product cache");
  };

  const flushProduct = async () => {
    const res = await clearProduct({ ...auth(), code: codeInput.trim() });
    if (res?.data?.status) {
      toast.success(res.data.message);
      setProductResult(null);
    } else {
      toast.error(res?.data?.message || "Failed to flush product cache");
    }
  };

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          {/* Page Header */}
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Cache Management
            <LinksComponent />
          </div>

          <div className="p-3 space-y-4">
            {/* Status Section */}
            <div className="p-4 w-full bg-white shadow rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold text-gray-800">Cache Status</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    View live TTL and last-flush time for each loyalty engine cache.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchStatus}
                    disabled={loadingStatus}
                    className="text-sm font-semibold px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-50"
                  >
                    {loadingStatus ? "Loading..." : status ? "Refresh" : "Load Status"}
                  </button>
                  <button
                    onClick={handleFlushAll}
                    disabled={flushingKey === "all"}
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {flushingKey === "all" ? "Flushing..." : "Flush All Caches"}
                  </button>
                </div>
              </div>

              {status ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <CacheCard
                    label="Rules"
                    data={status.rules}
                    onFlush={() => flush("rules", clearRules, "rules")}
                    flushing={flushingKey === "rules"}
                  />
                  <CacheCard
                    label="Schemes"
                    data={status.schemes}
                    onFlush={() => flush("schemes", clearSchemes, "schemes")}
                    flushing={flushingKey === "schemes"}
                  />
                  <CacheCard
                    label="Company Data"
                    data={status.company}
                    onFlush={() => flush("company", clearCompany, "company")}
                    flushing={flushingKey === "company"}
                  />
                  <CacheCard
                    label="User Profiles"
                    data={status.userProfiles}
                    onFlush={() => flush("users-all", clearAllUsers, "user profiles")}
                    flushing={flushingKey === "users-all"}
                  />
                  <CacheCard
                    label="Products"
                    data={status.products}
                    onFlush={() => flush("products-all", clearAllProducts, "products")}
                    flushing={flushingKey === "products-all"}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-28 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                  <p className="text-sm text-gray-400">
                    Click <span className="font-semibold text-gray-600">Load Status</span> to check current cache state.
                  </p>
                </div>
              )}
            </div>

            {/* Individual Key Lookup */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* User Profile Lookup */}
              <div className="p-4 w-full bg-white shadow rounded-lg">
                <h2 className="text-base font-bold text-gray-800 mb-1">User Profile Cache</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Look up or flush the loyalty profile cache for a specific user by mobile number.
                </p>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Enter mobile number"
                    value={mobileInput}
                    onChange={(e) => { setMobileInput(e.target.value); setUserResult(null); }}
                    onKeyDown={(e) => e.key === "Enter" && lookupUser()}
                    className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={lookupUser}
                    disabled={loadingUser || !mobileInput.trim()}
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loadingUser ? "..." : "Lookup"}
                  </button>
                </div>

                {userResult && (
                  <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 text-xs space-y-2">
                    {userResult.name && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Name</span>
                        <span className="font-semibold text-gray-800">{userResult.name}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">App User ID</span>
                      <span className="font-mono text-gray-700">{userResult.appUserId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile</span>
                      <span className="font-mono text-gray-700">{userResult.mobile}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                      <span className="text-gray-500">Cache Status</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge active={userResult.cache?.active ?? false} />
                        {userResult.cache?.ttlSeconds != null && (
                          <span className="font-mono text-gray-600">{fmtTtl(userResult.cache.ttlSeconds)} left</span>
                        )}
                      </div>
                    </div>
                    {userResult.cache?.active && (
                      <button
                        onClick={flushUser}
                        disabled={clearingUser}
                        className="w-full mt-1 text-xs font-semibold py-1.5 px-3 rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {clearingUser ? "Flushing..." : "Flush This User's Cache"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Product Cache Lookup */}
              <div className="p-4 w-full bg-white shadow rounded-lg">
                <h2 className="text-base font-bold text-gray-800 mb-1">Product Cache</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Look up or flush the product cache for a specific product by its code.
                </p>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Enter product code"
                    value={codeInput}
                    onChange={(e) => { setCodeInput(e.target.value); setProductResult(null); }}
                    onKeyDown={(e) => e.key === "Enter" && lookupProduct()}
                    className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={lookupProduct}
                    disabled={loadingProduct || !codeInput.trim()}
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {loadingProduct ? "..." : "Lookup"}
                  </button>
                </div>

                {productResult && (
                  <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 text-xs space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Product Code</span>
                      <span className="font-mono text-gray-700">{productResult.productCode}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                      <span className="text-gray-500">Cache Status</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge active={productResult.cache?.active ?? false} />
                        {productResult.cache?.ttlSeconds != null && (
                          <span className="font-mono text-gray-600">{fmtTtl(productResult.cache.ttlSeconds)} left</span>
                        )}
                      </div>
                    </div>
                    {productResult.cache?.active && (
                      <button
                        onClick={flushProduct}
                        disabled={clearingProduct}
                        className="w-full mt-1 text-xs font-semibold py-1.5 px-3 rounded-md border border-red-300 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {clearingProduct ? "Flushing..." : "Flush This Product's Cache"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
